import classNames from 'classnames';
import noop from 'lodash/noop';
import React, {
  memo,
  RefObject,
  useCallback,
  useContext,
  useMemo,
  useState
} from 'react';
import ReactSelect, { components, MenuListComponentProps } from 'react-select';
import { WrappedFieldProps } from 'redux-form';
import styled from 'styled-components';
import Label from './Label';
import colors from '../colors.json';
import { useScrollTogether } from 'helpers/hooks';
import PageBuilderContext from '../contexts/page-builder';
import FormBuilderContext from '../contexts/form-builder';

const MenuList = (props: MenuListComponentProps<{}>) => {
  const { innerRef } = props;
  const [listEl, setListEl] = useState<HTMLElement | null>(null);
  const { formRef } = useContext(FormBuilderContext);
  const formEl = (formRef as RefObject<HTMLFormElement>)?.current;
  const { pageRef } = useContext(PageBuilderContext);
  const pageEl = (pageRef as RefObject<HTMLDivElement>)?.current;
  useScrollTogether(listEl, pageEl || formEl);
  return (
    <components.MenuList
      {...props}
      innerRef={ref => {
        setListEl(ref);
        (innerRef as (instance: HTMLElement) => void)(ref);
      }}
    />
  );
};

const StyledReactSelect = styled(ReactSelect)`
  width: 98.6%;
  margin: auto;
  .react-select__control {
    color: red;
    padding: 0.5rem;
    background: ${colors.gray[200]};
    border: none;
    border-radius: 0.5rem;
  }

  .react-select__single-value,
  .react-select__input {
    color: ${colors.gray[900]};
  }

  .react-select__placeholder {
    color: ${colors.gray[500]};
  }

  .react-select__value-container,
  .react-select__indicator {
    padding: 0;
  }

  .react-select__menu {
    z-index: 1000;
  }

  .react-select__indicator {
    padding: 8px;
  }
`;

type SelectOption = { label: string; value: string };

const createSelectOptionGetter = (options: SelectOption[]) => {
  const cachedSelected: { [value: string]: SelectOption } = {};
  options.forEach(item => {
    const { value } = item;
    cachedSelected[value] = item;
  });
  return (value: string) => {
    if (value in cachedSelected) {
      return cachedSelected[value];
    }
    return options.find(option => option.value === value);
  };
};

type SelectProps = {
  className?: string;
  label?: string;
  onInputChange?: () => void;
  options: SelectOption[];
} & WrappedFieldProps;

const Select = (props: SelectProps) => {
  const {
    input: { value, onChange, onBlur, ...restInput },
    meta: { error },
    options,
    onInputChange = noop,
    ...restProps
  } = props;
  const getSelectValue = useMemo(() => createSelectOptionGetter(options), [
    options
  ]);
  // const onSelectBlur = useCallback(
  //   () => {
  //     const timeout = setTimeout(
  //       () => {
  //         onBlur(value);
  //         clearTimeout(timeout);
  //       },
  //       0,
  //     );
  //   },
  //   [onBlur, value],
  // );
  const onSelectChange = useCallback(
    newSelected => {
      onChange(newSelected.value);
      onInputChange(newSelected);
    },
    [onChange, onInputChange]
  );
  return (
    <Label {...props}>
      <StyledReactSelect
        classNamePrefix="react-select"
        className={classNames(
          'rounded-sm bg-gray-200 text-gray-900 outline-none',
          { 'border border-red-600': !!error }
        )}
        onChange={onSelectChange}
        // onBlur={onSelectBlur}
        value={getSelectValue(value)}
        options={options}
        components={{ MenuList }}
        {...restInput}
        {...restProps}
      />
    </Label>
  );
};

export default memo(Select);
